import React from "react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent as RawSheetContent,
  SheetHeader as RawSheetHeader,
  SheetTitle as RawSheetTitle,
} from "@shared/components/ui/sheet";
import { Button } from "@shared/components/ui/button";
import { formatHkt, formatUtc } from "@shared/lib/datetime";
import type { AuditEvent } from "@shared/api/audit";

// `sheet.jsx` is an untyped shadcn primitive — tsc can't infer its forwardRef
// prop types, so children/className/side are lost when consumed from .tsx.
// Re-type the three wrappers we use (runtime-accurate per sheet.jsx) so this
// file typechecks without touching the shared primitive. `Sheet` itself is
// Radix Dialog Root (already typed) and needs no shim.
type SheetDivProps = React.PropsWithChildren<{ className?: string }>;
const SheetContent = RawSheetContent as unknown as React.FC<
  SheetDivProps & { side?: "top" | "bottom" | "left" | "right" }
>;
const SheetHeader = RawSheetHeader as unknown as React.FC<SheetDivProps>;
const SheetTitle = RawSheetTitle as unknown as React.FC<SheetDivProps>;

// F-E Phase 4b — row detail drawer (first shadcn Sheet use in the app).
// "Open in Kibana" is intentionally absent (dropped to wish list, batch L);
// the correlation-id copy covers the tech-team grep need.

interface Props {
  event: AuditEvent | null;
  onClose: () => void;
}

async function copy(text: string, label: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Copy failed");
  }
}

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="grid grid-cols-[110px_1fr] gap-2 py-1 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="break-all">{children}</span>
  </div>
);

function jsonBlock(value: Record<string, unknown> | null): React.ReactNode {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <pre className="bg-muted/50 rounded p-2 text-xs overflow-x-auto">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

const AuditDetailDrawer: React.FC<Props> = ({ event, onClose }) => (
  <Sheet open={!!event} onOpenChange={(open) => !open && onClose()}>
    <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
      {event && (
        <div data-testid="audit-detail-drawer">
          <SheetHeader>
            <SheetTitle>Event detail</SheetTitle>
          </SheetHeader>

          <div className="mt-4 divide-y divide-border">
            <Row label="ID">
              <span className="font-mono text-xs">{event.id}</span>
            </Row>
            <Row label="When">
              <div>
                {formatHkt(event.ts)} HKT
                <div className="text-muted-foreground text-xs">
                  {formatUtc(event.ts)} UTC
                </div>
              </div>
            </Row>
            <Row label="Actor">
              {event.actorId}
              {event.actorRole ? ` (${event.actorRole})` : ""}
            </Row>
            <Row label="Action">
              <span className="font-mono text-xs">{event.action}</span>
            </Row>
            <Row label="Resource">
              {event.resourceKind
                ? `${event.resourceKind}${event.resourceId ? ` / ${event.resourceId}` : ""}`
                : "—"}
            </Row>
            <Row label="Decision">
              {event.decision === "allow"
                ? "✓ allow"
                : event.decision === "deny"
                  ? `✗ deny${event.denyReason ? ` — ${event.denyReason}` : ""}`
                  : "—"}
            </Row>
            <Row label="Correlation">
              <span className="font-mono text-xs">{event.correlationId}</span>
              <Button
                variant="ghost"
                size="sm"
                className="ml-2 h-6 px-2"
                onClick={() => copy(event.correlationId, "Correlation ID")}
                data-testid="audit-copy-correlation"
              >
                📋 Copy
              </Button>
            </Row>
            <Row label="Request">
              <div className="text-xs">
                <div>IP: {event.requestIp || "—"}</div>
                <div className="break-all">UA: {event.userAgent || "—"}</div>
              </div>
            </Row>
          </div>

          <div className="mt-4">
            <p className="text-sm text-muted-foreground mb-1">Changes</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs mb-1">Before</p>
                {jsonBlock(event.before ?? null)}
              </div>
              <div>
                <p className="text-xs mb-1">After</p>
                {jsonBlock(event.after ?? null)}
              </div>
            </div>
          </div>

          {event.metadata && Object.keys(event.metadata).length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground mb-1">Metadata</p>
              {jsonBlock(event.metadata)}
            </div>
          )}

          <div className="mt-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                copy(
                  `${window.location.origin}/staff/audit?event=${event.id}`,
                  "Deep link",
                )
              }
              data-testid="audit-copy-deeplink"
            >
              🔗 Copy deep link
            </Button>
          </div>
        </div>
      )}
    </SheetContent>
  </Sheet>
);

export default AuditDetailDrawer;
